import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Film, Play, Plus, RotateCcw } from 'lucide-react';
import generationApi, { mediaUrl } from '../lib/generationApi';

function FilmPreview({ item }) {
  const [source, setSource] = useState('');
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [started, setStarted] = useState(false);
  const player = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    setSource(''); setFailed(false); setStarted(false);
    async function load() {
      try {
        const data = await generationApi.get(item.id, controller.signal);
        if (!alive) return;
        const artifact = data.snapshot?.state === 'succeeded' && data.snapshot.result;
        if (!artifact?.artifact_id) { setFailed(true); return; }
        await generationApi.prepareMedia();
        if (alive) setSource(mediaUrl(item.id, artifact.artifact_id));
      } catch (_) { if (alive) setFailed(true); }
    }
    load();
    return () => { alive = false; controller.abort(); };
  }, [item.id, attempt]);

  useEffect(() => {
    if (!source) return undefined;
    const timer = setInterval(() => { generationApi.prepareMedia().catch(() => {}); }, 10 * 60 * 1000);
    return () => clearInterval(timer);
  }, [source]);

  return <article className="studio-desk-film">
    <div className="studio-desk-film-player">
      {source && !failed ? <><video ref={player} className={started ? 'has-started' : ''} aria-label={`Preview ${item.name}`} data-dashboard-film controls={started} playsInline preload="metadata" src={source} onError={() => setFailed(true)} onPlay={event => { setStarted(true); document.querySelectorAll('video[data-dashboard-film]').forEach(element => { if (element !== event.currentTarget) element.pause(); }); }} onLoadedMetadata={event => { const element = event.currentTarget; if (Number.isFinite(element.duration) && element.duration > 0.2) element.currentTime = 0.1; }} />{!started && <button type="button" className="studio-desk-film-play" aria-label={`Play ${item.name}`} onClick={() => player.current?.play().catch(() => setFailed(true))}><Play size={23} fill="currentColor" aria-hidden /></button>}</> : <div className="studio-desk-film-placeholder"><Film size={28} aria-hidden /><p>{failed ? 'Open the film to view the full result.' : 'Loading your film preview...'}</p>{failed && <button type="button" onClick={() => setAttempt(value => value + 1)}><RotateCcw size={14} aria-hidden />Retry preview</button>}</div>}
    </div>
    <Link to={`/runs/${item.id}`} className="studio-desk-film-caption"><div><h3>{item.name}</h3><span>Open final film</span></div><ArrowUpRight size={19} aria-hidden /></Link>
  </article>;
}

export default function DashboardFilms({ items }) {
  const recent = items.filter(item => !item.historical && item.status === 'completed').sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 2);
  return <section className="studio-desk-showcase" aria-labelledby="desk-showcase-title">
    <div className="studio-desk-section-heading"><h2 id="desk-showcase-title">{recent.length ? 'Fresh from your production desk' : 'Your next film starts here'}</h2><a href="#creation-history" className="studio-text-link">View all creations <ArrowUpRight size={15} aria-hidden /></a></div>
    <div className={`studio-desk-showcase-grid ${recent.length ? '' : 'is-empty'}`}>
      {recent.map(item => <FilmPreview key={item.id} item={item} />)}
      <div className="studio-desk-start"><div><h3>A new product.<br /> A new possibility.</h3><p>Bring 1–6 product photos and an idea. Make a 15, 30, or 45 second film.</p></div><Link to="/create" className="studio-button studio-button-secondary"><Plus size={17} aria-hidden />Start a production</Link><span>Product photos → Your brief → Your film</span></div>
    </div>
  </section>;
}
